import React, { useState, useEffect } from "react";
import { X, Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../styles/datepicker-custom.css";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import ServiceSelection from "./calendar/ServiceSlection";
import SunbedSelection from "./SunbedSelection";
import CustomerSelection from "./calendar/CustomerSelection";
import SummaryStep from "./calendar/SummaryStep";
import AddCustomerStep from "./calendar/AddCustomerStep";
import PaymentStep from "./PaymentStep";
import FinalPaymentStep from "./FinalPaymentStep";
import PosPaymentStep from "./PosPaymentStep";
import BackButton from "./calendar/BackButton";
import TimeSpinner from "./ui/TimeSpinner";
import { useProducts } from "../context/ProductsContext";
import { useAuth } from "../auth/AuthContext";
import { useEmployees } from "../hooks/useEmployees";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../lib/utils";

import { getCustomerQuickDetails } from "../services/bookings";
import { getBusinessServices } from "../services/businessServicesApi";
import { storePosBooking } from "../services/posApi";
import { useToast } from "../hooks/useToast";
import GlobalLoader from "./ui/GlobalLoader";
import { Employee } from "../types/employee";
import { Customer } from "../types/customer";

interface Service {
  id: string;
  name: string;
  duration: string;
  price: string;
  sunbedId?: number | string;
  selectedPricingId?: number | string;
  selectedMinutes?: number;
  pricing?: Array<{
    id: number | string;
    type: "fixed" | "per_minute";
    value: string;
    price: number | string;
  }>;
  company_id?: number;
  staffIds?: number[];
}


interface AppointmentSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  staffList: { id: number; name: string; color: string }[];

  // Context Mode
  mode?: "dashboard" | "calendar";
  initialServiceType?: "Services" | "Sunbeds";

  // Calendar Context Props (Optional)
  stylist?: string;
  appointmentTime?: string;
  appointmentDate?: string | Date;
  services?: Service[];
  bookingData?: any;
  onRebook?: () => void;
  setFilteredServices?: (services: Service[]) => void;
  servicesLoading?: boolean;
  onRefresh?: () => void;
}

const AppointmentSidebar: React.FC<AppointmentSidebarProps> = ({
  isOpen,
  onClose,
  staffList,
  mode = "calendar",
  initialServiceType = "Services",
  stylist,
  appointmentTime,
  appointmentDate,
  services: calendarServices,
  bookingData,
  onRebook = () => { },
  setFilteredServices: setCalendarFilteredServices,
  servicesLoading: calendarServicesLoading,
  onRefresh,
}) => {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";
  const toast = useToast();
  const { setProducts } = useProducts();
  const { employees } = useEmployees();
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerDetails, setCustomerDetails] = useState<Customer | null>(null);
  const [selectedStylistId, setSelectedStylistId] = useState<number | null>(null);
  const [selectedStylist, setSelectedStylist] = useState<string>("");
  const [selectedDateTime, setSelectedDateTime] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0);
  });
  const [step, setStep] = useState<
    "selection" | "customer" | "summary" | "payment" | "finalPayment" | "addCustomer" | "posPayment"
  >("selection");
  const [tip, setTip] = useState<number>(0);
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const [isAddingService, setIsAddingService] = useState(false);
  const [dashboardFilteredServices, setDashboardFilteredServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectionMode, setSelectionMode] = useState<"Services" | "Sunbeds">(initialServiceType);

  // Sync state when sidebar opens or props change
  useEffect(() => {
    if (isOpen) {
      setSelectionMode(initialServiceType || "Services");
      const initialStylist = stylist || staffList[0]?.name || "";
      setSelectedStylist(initialStylist);

      // Find the ID of the initial stylist to pass to ServiceSelection
      const initialStylistObj = staffList.find(s => s.name === initialStylist);
      if (initialStylistObj) {
        setSelectedStylistId(initialStylistObj.id);
      }

      let currentInitDate = selectedDateTime.toLocaleDateString("en-CA");
      let currentInitTime = selectedDateTime.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });

      if (mode === "calendar") {
        if (bookingData) {
          setSelectedServices(bookingData.services || (bookingData.service ? [bookingData.service] : []));
          setSelectedCustomer(bookingData.customer);
          setStep("summary");
        } else {
          setSelectedServices([]);
          setSelectedCustomer(null);
          setStep("selection");
        }
        if (appointmentDate) {
          let baseDate: Date;
          if (typeof appointmentDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(appointmentDate)) {
            const [y, m, d] = appointmentDate.split("-").map(Number);
            baseDate = new Date(y, m - 1, d, 12, 0, 0); // Noon Local
          } else {
            const dateToParse = typeof appointmentDate === 'string' ? appointmentDate.replace(" ", "T") : appointmentDate;
            baseDate = new Date(dateToParse);
          }

          if (!isNaN(baseDate.getTime())) {
            setSelectedDateTime(baseDate);
            currentInitDate = baseDate.toLocaleDateString("en-CA");
          }
          currentInitTime = appointmentTime || currentInitTime;
        }
      } else {
        setSelectedServices([]);
        setSelectedCustomer(null);
        setStep("selection");
        const now = new Date();
        setSelectedDateTime(now);
        currentInitDate = now.toLocaleDateString("en-CA");
        currentInitTime = now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
      }

      handleStylistChange(initialStylist, currentInitDate, currentInitTime);
    } else {
      // Reset on close
      setProducts([]);
      setSelectedCustomer(null);
      setCustomerDetails(null);
      setIsAddingService(false);
    }
  }, [isOpen, initialServiceType, mode, stylist, appointmentDate, bookingData]);

  const handleStylistChange = async (stylistName: string, overrideDate?: string, overrideTime?: string) => {
    setSelectedStylist(stylistName);
    const employee = employees.find((emp: Employee) => emp.name === stylistName);
    const employeeId = employee?.id ? Number(employee.id) : (staffList.find(s => s.name === stylistName)?.id || 1);
    setSelectedStylistId(employeeId);

    const dateStr = overrideDate || selectedDateTime.toISOString().split("T")[0];
    const timeStr = overrideTime || selectedDateTime.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });

    setLoading(true);
    try {
      const allServices = await getBusinessServices();
      // Store all services with their providing staff IDs
      const mappedServices = allServices.map(s => ({
        id: s.id,
        name: s.name,
        duration: s.durationLabel,
        price: String(s.price),
        company_id: Number((s.raw as any)?.company_id || 0),
        staffIds: s.staff.map(m => Number(m.id)),
        categoryId: (s.raw as any)?.category_id,
        locationId: (s.raw as any)?.location_id,
        description: (s.raw as any)?.description,
        discount: (s.raw as any)?.discount,
        discount_type: (s.raw as any)?.discount_type,
        service_type: (s.raw as any)?.service_type,
        tax_ids: (s.raw as any)?.service_taxes?.map((t: any) => t.tax_id || t.id) || []
      }));

      if (mode === "calendar" && setCalendarFilteredServices) {
        setCalendarFilteredServices(mappedServices);
      } else {
        setDashboardFilteredServices(mappedServices);
      }
    } catch (err) {
      console.error("Error fetching services:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = () => {
    if (selectedServices.length > 0 && selectedCustomer) {
      setStep("posPayment");
    }
  };

  const goBack = () => {
    const backMap: Record<string, any> = {
      customer: "selection",
      addCustomer: "customer",
      summary: "customer",
      payment: "summary",
      finalPayment: "payment",
      posPayment: "summary",
    };
    if (backMap[step]) {
      if (step === "posPayment") setProducts([]);
      setStep(backMap[step]);
    } else if (step === "selection" && isAddingService) {
      setIsAddingService(false);
      setStep("summary");
    }
  };

  const handlePosBack = () => {
    setStep("summary");
    setProducts([]);
  };

  // Fetch detailed customer info when reaching summary
  useEffect(() => {
    const fetchDetails = async () => {
      if (step === "summary" && selectedCustomer) {
        // If it's a walk-in customer, clear the detailed info and return
        if (selectedCustomer.id === 0) {
          setCustomerDetails(null);
          return;
        }

        // If current details don't match the selected customer ID, clear them
        // to avoid showing stale info while fetching
        if (customerDetails && customerDetails.id !== selectedCustomer.id) {
          setCustomerDetails(null);
        }

        try {
          const response = await getCustomerQuickDetails(selectedCustomer.id);
          if (response.success && response.data) {
            setCustomerDetails({
              id: response.data.id,
              name: response.data.name,
              email: response.data.email || "",
              dob: response.data.date_of_birth || undefined,
              phone: response.data.formatted_mobile || undefined,
              user_image_url: response.data.user_image_url || undefined,
            });
          }
        } catch (err) {
          console.error("Failed to fetch customer quick details:", err);
        }
      }
    };
    fetchDetails();
  }, [step, selectedCustomer]);

  const currentStepIndex = step === "selection" ? 0 : (step === "customer" || step === "addCustomer") ? 1 : step === "summary" ? 2 : 3;
  const steps = ["Selection", "Customer", "Summary", "Payment"];

  // --- Render Helpers ---

  const getEffectiveTime = () => {
    return mode === "calendar"
      ? appointmentTime!
      : selectedDateTime.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true });
  };

  const renderSelectionStep = () => {
    if (selectionMode === "Sunbeds") {
      return (
        <SunbedSelection
          setSelectedServices={setSelectedServices}
          setStep={setStep}
          isAddingService={isAddingService}
          setIsAddingService={setIsAddingService}
          disabledServices={selectedServices}
        />
      );
    }

    if (loading || calendarServicesLoading) {
      return (
        <div className="flex flex-1 items-center justify-center h-[300px]">
          <GlobalLoader initialPageLoad={false} compensateSidebar={false} description="Updating services..." />
        </div>
      );
    }

    return (
      <ServiceSelection
        services={(mode === "calendar" ? calendarServices : dashboardFilteredServices) || []}
        setSelectedServices={setSelectedServices}
        setStep={setStep}
        isAddingService={isAddingService}
        setIsAddingService={setIsAddingService}
        disabledServices={selectedServices}
        selectedStylistId={selectedStylistId || undefined}
      />
    );
  };

  const renderStepContent = () => {
    const commonProps = {
      selectedServices,
      selectedCustomer: selectedCustomer!,
      appointmentTime: getEffectiveTime(),
      tip,
      setTip,
      goBack,
      staffList,
      stylist: selectedStylist,
    };

    const motionProps = {
      initial: { opacity: 0, x: 10 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: -10 },
      transition: { duration: 0.3 },
      className: cn(
        "flex-1 overflow-y-auto",
        step === "payment" || step === "finalPayment" || step === "summary" ? "" : "p-4 sm:p-6"
      )
    };

    switch (step) {
      case "selection":
        return (
          <motion.div key="selection" {...motionProps}>
            {renderSelectionStep()}
          </motion.div>
        );

      case "customer":
        return (
          <motion.div key="customer" {...motionProps}>
            <CustomerSelection
              setSelectedCustomer={setSelectedCustomer}
              setStep={setStep}
              selectedServices={selectedServices}
            />
          </motion.div>
        );

      case "addCustomer":
        return (
          <motion.div key="addCustomer" {...motionProps}>
            <AddCustomerStep
              setSelectedCustomer={setSelectedCustomer}
              setStep={setStep}
            />
          </motion.div>
        );

      case "summary": {
        if (!selectedCustomer) return null;

        const handleSaveAsBooked = async () => {
          if (!selectedCustomer || selectedServices.length === 0) return;
          try {
            const serviceIds = selectedServices
              .filter(s => !s.sunbedId)
              .map(s => {
                let id = s.id;
                if (typeof id === 'string' && id.startsWith('service-')) {
                  id = id.replace('service-', '');
                }
                return id;
              });

            const serviceQuantities = selectedServices
              .filter(s => !s.sunbedId)
              .map(() => 1);

            const servicePrices = selectedServices
              .filter(s => !s.sunbedId)
              .map(s => typeof s.price === "string" ? parseFloat(s.price.replace(/[£$€]/g, "")) : Number(s.price || 0));

            const sunbedPrices = selectedServices
              .filter(s => !!s.sunbedId)
              .map(s => typeof s.price === "string" ? parseFloat(s.price.replace(/[£$€]/g, "")) : Number(s.price || 0));

            const totalAmount = [...servicePrices, ...sunbedPrices].reduce((a, b) => a + b, 0);

            const serviceTaxPercents = selectedServices
              .filter(s => !s.sunbedId)
              .map(() => 0);

            const formatToHiA = (timeStr: string): string => {
              if (!timeStr) return "";
              const normalized = timeStr.split("–")[0].replace(".", ":").trim();
              if (normalized.toLowerCase().includes("am") || normalized.toLowerCase().includes("pm")) {
                const parts = normalized.split(" ");
                const timePart = parts[0];
                const ampm = parts[1] || (normalized.toLowerCase().includes('pm') ? 'pm' : 'am');
                const [h, m] = timePart.split(":");
                return `${h.padStart(2, "0")}:${(m || "00").padEnd(2, "0")} ${ampm.toLowerCase()}`;
              }
              return timeStr;
            };

            const finalPosDate = selectedDateTime.toLocaleDateString("en-CA");
            const finalPosTime = formatToHiA(getEffectiveTime());

            // Prepare d-m-Y format
            const parts = finalPosDate.split("-");
            const dateDMY = `${parts[2]}-${parts[1]}-${parts[0]}`;

            const payload = {
              cart_services: serviceIds.length > 0 ? serviceIds : undefined,
              cart_quantity: serviceQuantities.length > 0 ? serviceQuantities : undefined,
              cart_prices: servicePrices.length > 0 ? servicePrices : undefined,
              tax_percent: serviceTaxPercents.length > 0 ? serviceTaxPercents : undefined,
              calendar_sunbeds: selectedServices.filter(s => s.sunbedId).map(s => ({
                id: s.sunbedId!,
                pricing: s.pricing || [],
                selectedPricingId: s.selectedPricingId!,
                selectedMinutes: s.selectedMinutes,
                company_id: s.company_id || user?.company?.id
              })),
              location: user?.company?.location_id || 1,
              pos_date: finalPosDate,
              pos_time: finalPosTime,
              date: dateDMY,
              time: finalPosTime,
              posTime: finalPosTime,
              user_id: selectedCustomer.id || 0,
              payments: [{ type: "cash" as const, amount: 0 }],
              payment_gateway: "Cash",
              pending_amount: totalAmount,
              amount_paid: 0,
              change_due: "0.00",
              type: "pay_later" as const,
              serviceType: "offline" as const,
              total_tax: 0,
              cart_discount: 0,
              coupon_id: "",
              coupon_amount: "",
              coupon: "",
              uuid: null,
              transaction_id: null,
              employee: selectedStylistId ? [selectedStylistId] : (staffList.length > 0 ? [staffList[0].id] : undefined),
            };

            // Calculate total tax
            const calculatedTotalTax = 0;
            payload.total_tax = calculatedTotalTax;

            // Ensure pending_amount is formatted as number
            payload.pending_amount = Number(totalAmount.toFixed(2));

            console.log("---------------------------------------------------------------------DEBUG: handleSaveAsBooked Payload", payload);

            await storePosBooking(payload);
            toast.success("Booking saved successfully!");
            if (onRefresh) onRefresh();
            onClose();
          } catch (error: any) {
            console.error("Save as booked error details:", {
              message: error.message,
              response: error.response?.data,
              status: error.response?.status
            });
            const errorMessage = error.response?.data?.message || error.message || "Failed to save booking.";
            toast.error(errorMessage);
          }
        };

        return (
          <motion.div key="summary" {...motionProps} className="h-full">
            <SummaryStep
              selectedServices={selectedServices}
              selectedCustomer={customerDetails || selectedCustomer}
              bookingData={bookingData}
              onRebook={onRebook}
              setIsAddingService={setIsAddingService}
              setSelectionMode={setSelectionMode}
              selectionMode={selectionMode}
              setStep={(ns) => {
                if (ns === "payment") handlePayNow();
                else setStep(ns);
              }}
              appointmentTime={getEffectiveTime()}
              tip={tip}
              setTip={setTip}
              staffList={staffList}
              stylist={selectedStylist}
              onSaveAsBooked={handleSaveAsBooked}
              onRefresh={onRefresh}
              setSelectedServices={setSelectedServices}
              onClose={onClose}
            />
          </motion.div>
        );
      }

      case "payment": {
        if (!selectedCustomer) return null;
        return (
          <motion.div key="payment" {...motionProps}>
            <PaymentStep
              selectedCustomer={customerDetails || selectedCustomer}
              selectedServices={selectedServices}
              appointmentTime={getEffectiveTime()}
              tip={tip}
              setTip={setTip}
              goToFinalPayment={() => setStep("finalPayment")}
              bookingId={bookingData?.id}
              goBack={() => setStep("summary")}
              staffList={staffList}
              stylist={selectedStylist}
            />
          </motion.div>
        );
      }

      case "finalPayment": {
        if (!selectedCustomer) return null;
        return (
          <motion.div key="finalPayment" {...motionProps}>
            <FinalPaymentStep
              selectedCustomer={customerDetails || selectedCustomer}
              selectedServices={selectedServices}
              appointmentTime={getEffectiveTime()}
              onRebook={onRebook}
              bookingId={bookingData?.id}
              goBack={() => setStep("payment")}
              staffList={staffList}
              stylist={selectedStylist}
            />
          </motion.div>
        );
      }

      case "posPayment": {
        if (!selectedCustomer || selectedServices.length === 0) return null;

        const totalServicePrice = selectedServices.reduce(
          (sum, s) => sum + (typeof s.price === "string" ? parseFloat(s.price.replace(/[£$€]/g, "")) : Number(s.price)),
          0
        );

        return (
          <motion.div key="posPayment" {...motionProps} className="h-full">
            <PosPaymentStep
              selectedService={{
                id: selectedServices[0].id,
                name: selectedServices.map(s => s.name).join(", "),
                duration: selectedServices.map(s => s.duration).join(", "),
                price: `${currencySymbol}${totalServicePrice.toFixed(2)}`
              }}
              selectedCustomer={selectedCustomer.name}
              appointmentTime={getEffectiveTime()}
              tip={tip}
              setTip={setTip}
              goToFinalPayment={() => setStep("finalPayment")}
              onClose={onClose}
              mode="paynow"
              locationId={user?.company?.location_id || 1}
              userId={selectedCustomer.id}
              services={selectedServices.filter(s => !s.sunbedId).map(s => ({
                id: s.id,
                quantity: 1,
                price: typeof s.price === "string" ? parseFloat(s.price.replace(/[£$€]/g, "")) : Number(s.price),
                taxPercent: 0
              }))}
              sunbeds={selectedServices.filter(s => s.sunbedId).map(s => ({
                id: s.sunbedId!,
                pricing: s.pricing || [],
                selectedPricingId: s.selectedPricingId!,
                selectedMinutes: s.selectedMinutes,
                company_id: s.company_id || user?.company?.id
              }))}
              employees={selectedStylistId ? [selectedStylistId] : undefined}
              posDate={selectedDateTime.toLocaleDateString("en-CA")}
              onPaymentComplete={() => { setProducts([]); if (onRefresh) onRefresh(); onClose(); }}
              isEmbedded={true}
              onBack={handlePosBack}
            />
          </motion.div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[99]"
            onClick={step === "posPayment" ? handlePosBack : onClose}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: "100%", width: "500px" }}
            animate={{
              x: 0,
              width: (step === "summary" || step === "payment" || step === "finalPayment" || step === "posPayment")
                ? (window.innerWidth < 1024 ? "100%" : "60%")
                : (window.innerWidth < 1024 ? "100%" : "500px")
            }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className={cn(
              "fixed top-0 right-0 h-full bg-[#F9F9F9] shadow-[-10px_0_30px_rgba(0,0,0,0.05)] border-l border-gray-100 z-[100] flex flex-col origin-right"
            )}
          >
            {/* Close Button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="absolute lg:-left-14 top-4 lg:top-8 right-4 lg:right-auto w-10 h-10 flex items-center justify-center rounded-full bg-white text-[#885ABB] shadow-lg lg:shadow-[-4px_0_12px_rgba(0,0,0,0.1)] hover:bg-[#402B69] hover:text-white transition-all z-[110] group"
            >
              <X size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform duration-300" />
            </motion.button>

            {/* Header */}
            <AnimatePresence>
              {step !== "payment" && step !== "finalPayment" && step !== "summary" && step !== "posPayment" && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white border-b border-gray-100 shadow-sm z-10 sticky top-0"
                >
                  <div className="px-4 sm:px-8 pt-10 pb-6 flex items-center justify-between">
                    <div>
                      <h2 className="text-xl sm:text-[27px] font-[600] text-[#402B69] leading-tight pr-8 lg:pr-0">
                        {step === "addCustomer" ? "Add Customer" : selectionMode === "Sunbeds" ? "Add Sunbed Session" : "Add Appointment"}
                      </h2>
                      <p className="text-[10px] sm:text-[11px] text-gray-400 mt-1 font-semibold uppercase">
                        Step {currentStepIndex + 1} of 4: {steps[currentStepIndex]}
                      </p>
                    </div>
                    <div>
                      {(step !== "selection" || (step === "selection" && isAddingService)) && (
                        <BackButton onClick={goBack} />
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 px-4 sm:px-8 mb-6">
                    {[0, 1, 2, 3].map((s) => (
                      <div key={s} className={cn("h-1.5 flex-1 rounded-full transition-all duration-500", s <= currentStepIndex ? "bg-[#885ABB]" : "bg-gray-100")} />
                    ))}
                  </div>

                  {selectionMode !== "Sunbeds" && (
                    <div className="px-4 sm:px-8 pb-8 space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-2">
                          <label className="text-[11px] font-semibold text-gray-400 uppercase pl-1">Stylist</label>
                          <Select value={selectedStylist} onValueChange={handleStylistChange}>
                            <SelectTrigger className="h-12 border border-gray-200 bg-white rounded-xl text-sm font-semibold text-[#402B69] shadow-sm hover:border-[#885ABB] transition-all">
                              <SelectValue placeholder="Select stylist" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border border-gray-200 shadow-xl rounded-xl z-[100]">
                              {staffList.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="flex flex-col gap-2">
                          <label className="text-[11px] font-semibold text-gray-400 uppercase pl-1">Date & Time</label>
                          {mode === "dashboard" ? (
                            <DatePicker
                              selected={selectedDateTime}
                              onChange={(date: Date | null) => date && setSelectedDateTime(date)}
                              showTimeInput
                              customTimeInput={<TimeSpinner />}
                              dateFormat="dd MMM yyyy, h:mm aa"
                              className="h-12 w-full border border-gray-100 bg-white rounded-xl text-sm font-bold text-[#402B69] px-4 shadow-sm hover:border-[#885ABB] transition-all focus:outline-none"
                              calendarClassName="custom-datepicker"
                              popperPlacement="bottom-end"
                              portalId="root"
                              renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
                                <div className="flex items-center justify-between px-3 py-1 bg-[#885ABB] rounded-t-lg">
                                  <button onClick={decreaseMonth} type="button" className="p-1 text-white hover:bg-white/20 rounded-lg"><ChevronLeft size={18} /></button>
                                  <span className="text-white font-semibold text-xs uppercase">{date.toLocaleString("default", { month: "long", year: "numeric" })}</span>
                                  <button onClick={increaseMonth} type="button" className="p-1 text-white hover:bg-white/20 rounded-lg"><ChevronRight size={18} /></button>
                                </div>
                              )}
                            />
                          ) : (
                            <div className="h-12 border border-gray-100 bg-gray-50/50 rounded-xl flex items-center px-3 sm:px-4 gap-2 text-xs sm:text-sm font-bold text-gray-500">
                              <CalendarIcon size={16} className="shrink-0" />
                              <span className="whitespace-nowrap">{new Date(appointmentDate!).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                              <span className="w-px h-3 bg-gray-300 mx-0.5 sm:mx-1 shrink-0" />
                              <span className="whitespace-nowrap">{appointmentTime}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Steps Body */}
            <div className="flex-1 overflow-hidden flex flex-col relative">
              <AnimatePresence mode="wait">
                {renderStepContent()}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AppointmentSidebar;
